import { FC, useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import { treatmentPlanService } from "../../services/treatmentPlan.service";
import {
	PropagationJob,
	propagationBanner,
	shouldPollPropagation,
} from "./propagationStatus";

/**
 * SIMS-9033 — the state of a threshold change that is running, or that ran.
 *
 * The save returns before the cohort is written (AC1), so this is the ONLY place the administrator
 * ever learns what happened. It reads the persisted Job rather than anything held in the page,
 * which is what makes AC3 ("later or on another day") and AC4 (navigating away is safe) work
 * without doing anything special.
 */

const POLL_INTERVAL_MS = 2000;

export interface PropagationBannerProps {
	treatmentPlanId: string;
	/** Bumped by the parent after a change is applied, so the banner picks it up immediately. */
	refreshKey?: number;
}

const PropagationBanner: FC<PropagationBannerProps> = ({
	treatmentPlanId,
	refreshKey = 0,
}) => {
	const [job, setJob] = useState<PropagationJob | null>(null);
	const [retrying, setRetrying] = useState(false);

	const load = useCallback(async () => {
		try {
			setJob(
				await treatmentPlanService.getThresholdPropagationStatus(
					treatmentPlanId,
				),
			);
		} catch {
			// A status read that fails must not break the plan form — the administrator can still
			// edit the plan, and the outcome is durable on the Job whenever the read next succeeds.
			setJob(null);
		}
	}, [treatmentPlanId]);

	useEffect(() => {
		void load();
	}, [load, refreshKey]);

	// AC2 — keep asking only while work is outstanding. A finished result cannot change, so polling
	// it would be a background request per plan view for nothing.
	useEffect(() => {
		if (!shouldPollPropagation(job)) {
			return;
		}
		const timer = setInterval(() => void load(), POLL_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [job, load]);

	const banner = propagationBanner(job);

	if (!banner) {
		return null;
	}

	return (
		<Alert
			severity={banner.severity}
			sx={{ mb: 2 }}
			data-testid="propagation-banner"
		>
			<AlertTitle>{banner.title}</AlertTitle>
			{banner.detail}

			{/* SIMS-9034 AC1/AC4 — offered only when something is actually outstanding, so a clean
			    run does not imply that anything was left undone. */}
			{banner.canRetry && job?._id && (
				<Button
					size="small"
					variant="outlined"
					sx={{ mt: 1, display: "block" }}
					disabled={retrying}
					data-testid="propagation-retry"
					onClick={async () => {
						setRetrying(true);
						try {
							await treatmentPlanService.retryThresholdPropagation(
								treatmentPlanId,
								job._id as string,
							);
							await load();
						} catch (err) {
							// eslint-disable-next-line no-console
							console.error("Retry failed:", err);
						} finally {
							setRetrying(false);
						}
					}}
				>
					{retrying ? "Retrying…" : "Retry these participants"}
				</Button>
			)}
			{/* AC2: "how far it has got" — indeterminate, because the cohort size is not known to
			    the page and inventing a percentage would be worse than showing none. */}
			{!banner.isComplete && banner.severity === "info" && (
				<LinearProgress sx={{ mt: 1 }} />
			)}
		</Alert>
	);
};

export default PropagationBanner;
